import React from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';

import {BackToTopButton} from '../../../components/BackToTopButton';
import {ErrorContainer} from '../../../components/ErrorContainer';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {Constants} from '../../../constants';
import {resolveAssetUrl} from '../../../lib/api';
import {useAdaptiveSafeArea} from '../../../lib/hooks/useAdaptiveSafeArea';
import {useLoadPage} from '../../../lib/hooks/useLoadPages';
import {FixedFooter} from '../FixedFooter';
import {Layout} from '../composable';
import {HeroImage} from './HeroImage';
import {RichTextRenderer} from './RichTextRenderer';

export interface Props {
  screenName: string;
}

export const Page: React.FC<Props> = ({screenName}) => {
  const {page, loading, error} = useLoadPage({screenName});

  useAdaptiveSafeArea();

  const scrollViewRef = React.useRef<ScrollView>(null);

  if (loading) return <LoadingOverlay />;

  if (!page || !!error) return <ErrorContainer />;

  const fields = page.fields;

  return (
    <Layout style={styles.layout}>
      <ScrollView
        ref={scrollViewRef}
        showsVerticalScrollIndicator={false}
        style={styles.scroller}>
        {fields.heroImage && (
          <HeroImage
            source={{
              uri: resolveAssetUrl(fields.heroImage),
            }}
          />
        )}

        <View
          style={[
            styles.content,
            {paddingBottom: fields.fixedFooter ? 124 : 48},
          ]}>
          <RichTextRenderer content={fields?.content} />

          {!!fields.backToTopButton && (
            <BackToTopButton
              onPress={() =>
                scrollViewRef.current?.scrollTo({y: 0, animated: true})
              }
            />
          )}
        </View>
      </ScrollView>

      {!!fields.fixedFooter && (
        <FixedFooter>
          <RichTextRenderer content={fields.fixedFooter} />
        </FixedFooter>
      )}
    </Layout>
  );
};

const styles = StyleSheet.create({
  layout: {
    flexBasis: 0,
  },
  scroller: {
    flex: 1,
  },
  content: {
    paddingHorizontal: Constants.PAGE_HORIZONTAL_PADDING,
  },
  emptyParagraph: {
    height: 16,
  },
});
